// ApplyLoanPage.jsx (Fixed - Filters cleared loans, enforces total borrowing capacity across all loan types)

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// ── Searchable member picker ──────────────────────────────────────────────────
function MemberSearch({ members, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const selected = members.find(m => m.memberId === value);
  const displayLabel = selected
    ? `${selected.memberId} - ${selected.personalInfo?.fullName || ''}`
    : '';

  const inputValue = open ? query : displayLabel;

  const filtered = query.trim()
    ? members.filter(m => {
        const q = query.toLowerCase();
        return (
          m.memberId?.toLowerCase().includes(q) ||
          (m.personalInfo?.fullName || '').toLowerCase().includes(q)
        );
      })
    : members;

  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(m) { onChange(m.memberId); setQuery(''); setOpen(false); }
  function handleInputChange(e) { setQuery(e.target.value); setOpen(true); if (!e.target.value) onChange(''); }
  function handleFocus() { setQuery(''); setOpen(true); }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        placeholder="Type ID or name to search..."
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        autoComplete="off"
        style={{ width: '100%', padding: '6px 10px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '14px', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px', maxHeight: '260px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 4px 8px rgba(0,0,0,0.12)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px', color: '#888', fontSize: '13px' }}>No members found</div>
            : filtered.map(m => (
              <div
                key={m.id || m.memberId}
                onMouseDown={() => handleSelect(m)}
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', background: m.memberId === value ? '#ede9fe' : 'white', fontWeight: m.memberId === value ? 600 : 400, borderBottom: '1px solid #f0f0f0' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f3f0ff'}
                onMouseLeave={e => e.currentTarget.style.background = m.memberId === value ? '#ede9fe' : 'white'}
              >
                <span style={{ color: '#4b3ff6', fontWeight: 600 }}>{m.memberId}</span>{' - '}{m.personalInfo?.fullName || ''}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────
import AdminLayout from './AdminLayout';
import { writeActivityLog } from '../utils/activityLog';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const TODAY_ISO = new Date().toISOString().slice(0,10);
const INTEREST_RATE = 0.01;

const defaultFirst = {
  memberId: '',
  loanType: 'normal',
  originalAmount: '',
  disbursementDate: TODAY_ISO,
  duration: '',
  monthlyPrincipalPayment: '',
  applicationFee: ''
};

const defaultTop = {
  loanType: 'normal',
  originalAmount: '',
  disbursementDate: TODAY_ISO,
  duration: '',
  monthlyPrincipalPayment: '',
  applicationFee: ''
};

export default function ApplyLoanPage(){
  const navigate = useNavigate();
  const [tab, setTab] = useState('first');
  const [members, setMembers] = useState([]);
  const [first, setFirst] = useState({ ...defaultFirst });
  const [topMemberId, setTopMemberId] = useState('');
  const [top, setTop] = useState({ ...defaultTop });
  const [memberInfo, setMemberInfo] = useState(null);
  const [loanStatus, setLoanStatus] = useState(null);
  const [projectedShares, setProjectedShares] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(()=>{ loadMembers(); }, []);
  
  useEffect(()=>{
    const id = tab==='first' ? first.memberId : topMemberId;
    if (id) {
      fetchMemberInfo(id);
      fetchLoanStatus(id);
    } else {
      setMemberInfo(null);
      setLoanStatus(null);
      setProjectedShares(null);
    }
  }, [tab, first.memberId, topMemberId]);

  useEffect(() => {
    const id = tab === 'first' ? first.memberId : topMemberId;
    const date = tab === 'first' ? first.disbursementDate : top.disbursementDate;
    if (id && date) {
      fetchProjectedShares(id, date);
    }
  }, [tab, first.memberId, first.disbursementDate, topMemberId, top.disbursementDate]);

  async function loadMembers(){
    try{
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/members-for-statement`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const j = await r.json();
      if (j.success) setMembers(j.data?.members || []);
    }catch(e){}
  }

  async function fetchMemberInfo(memberId){
    try{
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/members/${encodeURIComponent(memberId)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      if (!r.ok) return setMemberInfo(null);
      const j = await r.json();
      setMemberInfo(j.data?.member || j.data);
    }catch(e){ setMemberInfo(null); }
  }

  async function fetchLoanStatus(memberId){
    try{
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/loans/members/${encodeURIComponent(memberId)}/loan-status`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      if (!r.ok) return setLoanStatus(null);
      const j = await r.json();
      if (j.success) {
        setLoanStatus(j.data);
      }
    }catch(e){ 
      setLoanStatus(null); 
    }
  }

  async function fetchProjectedShares(memberId, targetDate){
    try{
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/loans/members/${encodeURIComponent(memberId)}/projected-shares?targetDate=${targetDate}`, { 
        headers: token ? { Authorization: `Bearer ${token}` } : {} 
      });
      if (!r.ok) return setProjectedShares(null);
      const j = await r.json();
      if (j.success) {
        setProjectedShares(j.data);
      }
    }catch(e){ 
      setProjectedShares(null); 
    }
  }

  const memberOptions = (<>
    <option value="">-- Select --</option>
    {members.map(m => (
      <option key={m.id} value={m.memberId}>{m.memberId} - {m.personalInfo?.fullName || ''}</option>
    ))}
  </>);

  function getTrulyActiveLoans() {
    if (!loanStatus || !loanStatus.activeLoans) return [];
    return loanStatus.activeLoans.filter(loan => Number(loan.currentBalance) > 0);
  }

  function getTotalOutstandingDebt() {
    const trulyActiveLoans = getTrulyActiveLoans();
    return trulyActiveLoans.reduce((sum, loan) => sum + Number(loan.currentBalance || 0), 0);
  }

  function getRemainingBorrowingCapacity() {
    if (!projectedShares) return null;
    const maxCapacity = Number(projectedShares.borrowingCapacity || 0);
    const totalDebt = getTotalOutstandingDebt();
    const remaining = maxCapacity - totalDebt;
    return Math.max(0, remaining);
  }

  function calcPreview(amount, duration){
    const A = Number(amount || 0), N = Number(duration || 0);
    if (!(A>0 && N>0)) return null;
    const principal = Math.ceil(A/N);
    const interest = Math.round(A*INTEREST_RATE*100)/100;
    return { principal, interest, monthly: principal + interest };
  }

  const previewFirst = calcPreview(first.originalAmount, first.duration);
  const previewTop = calcPreview(top.originalAmount, top.duration);

  function getActiveLoansForType(loanType) {
    const trulyActiveLoans = getTrulyActiveLoans();
    return trulyActiveLoans.filter(loan => loan.loanType === loanType);
  }

  function hasExistingLoanOfType(loanType) {
    const activeLoans = getActiveLoansForType(loanType);
    return activeLoans.length > 0;
  }

  function canApplyForTopUp(loanType) {
    const activeLoans = getActiveLoansForType(loanType);
    if (activeLoans.length === 0) return { canApply: true, reason: 'No existing loan' };
    
    const qualifyingLoans = activeLoans.filter(loan => loan.qualifiesForTopUp);
    if (qualifyingLoans.length > 0) {
      return { canApply: true, reason: 'Qualified', loans: qualifyingLoans };
    }
    
    return { 
      canApply: false, 
      reason: 'Insufficient repayment', 
      loans: activeLoans 
    };
  }

  const remainingCapacity = getRemainingBorrowingCapacity();
  const currentAmount = tab === 'first' ? Number(first.originalAmount || 0) : Number(top.originalAmount || 0);
  const amountExceedsCapacity = remainingCapacity !== null && currentAmount > remainingCapacity;
  const totalOutstandingDebt = getTotalOutstandingDebt();

  async function submitFirst(e){
    e.preventDefault(); setBusy(true); setMsg({});
    try{
      if (hasExistingLoanOfType(first.loanType)) {
        throw new Error(`Cannot apply for "First Loan" - you already have existing ${first.loanType} loan(s). Please use the "Top-Up Loan" tab instead and ensure you have paid at least 75% of your existing loan.`);
      }
      
      if (remainingCapacity !== null && Number(first.originalAmount) > remainingCapacity) {
        throw new Error(`Amount exceeds remaining borrowing capacity. You can borrow up to KES ${remainingCapacity.toLocaleString()}. Total outstanding loans: KES ${totalOutstandingDebt.toLocaleString()}.`);
      }
      if (new Date(first.disbursementDate) < new Date(TODAY_ISO)) throw new Error('Disbursement cannot be in the past.');
      if (!first.applicationFee && first.applicationFee !== 0) throw new Error('Application Fee is required.');
      
      const payload = {
        memberId: first.memberId,
        loanType: first.loanType,
        originalAmount: Number(first.originalAmount),
        disbursementDate: first.disbursementDate,
        duration: Number(first.duration),
        monthlyPrincipalPayment: first.monthlyPrincipalPayment ? Number(first.monthlyPrincipalPayment) : undefined,
        applicationFee: first.applicationFee ? Number(first.applicationFee) : undefined,
        isConsolidation: false
      };
      const r = await globalApi.post('/loans/apply', payload);
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.message || 'Apply failed');
      setMsg({ type: 'success', text: '✅ Loan application submitted.' });
      writeActivityLog({
        source: 'apply_loan',
        memberId: first.memberId,
        memberName: members.find(m => m.memberId === first.memberId)?.personalInfo?.fullName || first.memberId,
        loanType: first.loanType,
        amount: Number(first.originalAmount),
        disbursementDate: first.disbursementDate,
        duration: Number(first.duration),
        applicationFee: first.applicationFee ? Number(first.applicationFee) : undefined,      });
      setFirst({ ...defaultFirst });
      if (first.memberId) {
        fetchLoanStatus(first.memberId);
        fetchProjectedShares(first.memberId, TODAY_ISO);
      }
    }catch(e){
      if (e.message === 'Session expired') {
        return;
      }
      setMsg({ type: 'error', text: e.message || 'Failed to submit' });
    }finally{ setBusy(false); }
  }

  async function submitTop(e){
    e.preventDefault(); setBusy(true); setMsg({});
    try{
      if (!topMemberId) throw new Error('Member required');
      
      const topUpCheck = canApplyForTopUp(top.loanType);
      if (!topUpCheck.canApply) {
        const loan = topUpCheck.loans[0];
        throw new Error(
          `Cannot apply for top-up. Existing ${loan.loanType} loan of KES ${loan.originalAmount.toLocaleString()} ` +
          `(taken on ${loan.disbursementDate}) has only ${loan.percentagePaid}% paid. ` +
          `Minimum 75% repayment required. Current balance: KES ${loan.currentBalance.toLocaleString()}.`
        );
      }
      
      if (remainingCapacity !== null && Number(top.originalAmount) > remainingCapacity) {
        throw new Error(`Amount exceeds remaining borrowing capacity. You can borrow up to KES ${remainingCapacity.toLocaleString()}. Total outstanding loans: KES ${totalOutstandingDebt.toLocaleString()}.`);
      }
      if (new Date(top.disbursementDate) < new Date(TODAY_ISO)) throw new Error('Disbursement cannot be in the past.');
      if (!top.applicationFee && top.applicationFee !== 0) throw new Error('Application Fee is required.');
      
      const payload = {
        memberId: topMemberId,
        loanType: top.loanType,
        originalAmount: Number(top.originalAmount),
        disbursementDate: top.disbursementDate,
        duration: Number(top.duration),
        monthlyPrincipalPayment: top.monthlyPrincipalPayment ? Number(top.monthlyPrincipalPayment) : undefined,
        applicationFee: top.applicationFee ? Number(top.applicationFee) : undefined,
        isConsolidation: true
      };
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/loans/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload)
      });
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.message || 'Top-up failed');
      setMsg({ type: 'success', text: '✅ Top-up submitted.' });
      writeActivityLog({
        source: 'apply_loan_topup',
        memberId: topMemberId,
        memberName: members.find(m => m.memberId === topMemberId)?.personalInfo?.fullName || topMemberId,
        loanType: top.loanType,
        amount: Number(top.originalAmount),
        disbursementDate: top.disbursementDate,
        duration: Number(top.duration),
        applicationFee: top.applicationFee ? Number(top.applicationFee) : undefined,      });
      setTop({ ...defaultTop });
      if (topMemberId) {
        fetchLoanStatus(topMemberId);
        fetchProjectedShares(topMemberId, TODAY_ISO);
      }
    }catch(e){
      setMsg({ type: 'error', text: e.message || 'Failed to submit' });
    }finally{ setBusy(false); }
  }

  const activeLoansForCurrentType = tab === 'first' 
    ? getActiveLoansForType(first.loanType) 
    : getActiveLoansForType(top.loanType);
  
  const hasExistingLoansForFirstTab = tab === 'first' && hasExistingLoanOfType(first.loanType);
  const topUpEligibility = tab === 'topup' ? canApplyForTopUp(top.loanType) : null;
  const trulyActiveLoans = getTrulyActiveLoans();
  const isFirstTabDisabled = busy || hasExistingLoansForFirstTab || amountExceedsCapacity;
  const isTopUpTabDisabled = busy || (topUpEligibility && !topUpEligibility.canApply) || amountExceedsCapacity;

  return (
    <AdminLayout activePage="apply" pageTitle="Apply Loan">
      {msg.text && <div className={msg.type==='error'?'alert alert-error':'alert alert-success'}>{msg.text}</div>}

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="tab-buttons">
              <button className={tab==='first'?'btn btn-primary':'btn btn-outline'} onClick={()=>setTab('first')}>
                First Loan / Initial Loan
              </button>
              <button className={tab==='topup'?'btn btn-primary':'btn btn-outline'} onClick={()=>setTab('topup')}>
                Top-Up Loan (Additional)
              </button>
            </div>
            <button
              type="button"
              onClick={() => navigate('/admin/activity-log')}
              style={{ padding: '8px 16px', fontSize: '14px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              📋 Log
            </button>
          </div>
        </div>

        <div className="card-body">
          {projectedShares && (
            <div className="info-box info-box-primary">
              <span className="info-box-title">💰 Borrowing Capacity</span>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Shares:</span>
                  <span className="info-value">KES {projectedShares.projectedShares.toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Limit (3×):</span>
                  <span className="info-value text-muted">KES {projectedShares.borrowingCapacity.toLocaleString()}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Outstanding:</span>
                  <span className={`info-value ${totalOutstandingDebt > 0 ? 'text-danger' : 'text-success'}`}>
                    KES {totalOutstandingDebt.toLocaleString()}
                  </span>
                </div>
                <div className="info-item">
                  <span className="info-label">Available:</span>
                  <span className={`info-value info-value-large ${remainingCapacity > 0 ? 'text-success' : 'text-danger'}`}>
                    KES {(remainingCapacity || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {trulyActiveLoans.length > 0 && (
            <div className="info-box info-box-warning">
              <span className="info-box-title">📊 Active Loans</span>
              {trulyActiveLoans.map((loan, idx) => (
                <div key={idx} className="loan-card">
                  <div className="loan-grid">
                    <div className="loan-item">
                      <span className="loan-label">Type:</span>
                      <span className="loan-value text-capitalize">{loan.loanType}</span>
                    </div>
                    <div className="loan-item">
                      <span className="loan-label">Amount:</span>
                      <span className="loan-value">KES {loan.originalAmount.toLocaleString()}</span>
                    </div>
                    <div className="loan-item">
                      <span className="loan-label">Date:</span>
                      <span className="loan-value">{loan.disbursementDate || 'N/A'}</span>
                    </div>
                    <div className="loan-item">
                      <span className="loan-label">Balance:</span>
                      <span className="loan-value text-danger">KES {loan.currentBalance.toLocaleString()}</span>
                    </div>
                    <div className="loan-item">
                      <span className="loan-label">Paid:</span>
                      <span className="loan-value text-success">KES {(loan.amountPaid || 0).toLocaleString()}</span>
                    </div>
                    <div className="loan-item">
                      <span className="loan-label">Progress:</span>
                      <span className={`loan-value loan-progress ${loan.percentagePaid >= 75 ? 'text-success' : loan.percentagePaid >= 50 ? 'text-warning' : 'text-danger'}`}>
                        {loan.percentagePaid}%
                      </span>
                    </div>
                  </div>
                  <div className="loan-status">
                    {loan.qualifiesForTopUp ? (
                      <span className="status-success">✅ Qualifies for Top-Up (≥75% paid)</span>
                    ) : (
                      <span className="status-danger">
                        ❌ No Top-Up (Need {(75 - loan.percentagePaid).toFixed(0)}% more)
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {hasExistingLoansForFirstTab && (
            <div className="info-box info-box-danger">
              <span className="info-box-title">🚫 Cannot Apply for First Loan</span>
              <p>You have {activeLoansForCurrentType.length} existing {first.loanType} loan(s). Use the <strong>Top-Up</strong> tab instead (requires 75% repayment).</p>
            </div>
          )}

          {tab === 'topup' && topUpEligibility && !topUpEligibility.canApply && (
            <div className="info-box info-box-danger">
              <span className="info-box-title">⚠️ Top-Up Not Available</span>
              <p>Pay at least <strong>75%</strong> of your existing {top.loanType} loan before applying for a top-up.</p>
            </div>
          )}

          {tab==='first' && (
            <form onSubmit={submitFirst}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Member *</label>
                  <MemberSearch members={members} value={first.memberId} onChange={v => setFirst({...first, memberId: v})} />
                </div>
                <div className="form-group">
                  <label>Loan Type *</label>
                  <select value={first.loanType} onChange={e=>setFirst({...first, loanType:e.target.value})}>
                    <option value="normal">Normal</option>
                    <option value="school">School</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  {remainingCapacity !== null && (
                    <div className={`capacity-hint ${remainingCapacity > 0 ? 'text-success' : 'text-danger'}`}>
                      Can borrow up to: KES {remainingCapacity.toLocaleString()}
                    </div>
                  )}
                  <input 
                    type="number" 
                    value={first.originalAmount} 
                    onChange={e=>setFirst({...first, originalAmount:e.target.value})} 
                    className={amountExceedsCapacity ? 'input-error' : ''}
                    required 
                  />
                  {amountExceedsCapacity && (
                    <span className="error-hint">⚠️ Amount exceeds borrowing capacity!</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Disbursement Date *</label>
                  <input type="date" min={TODAY_ISO} value={first.disbursementDate} onChange={e=>setFirst({...first, disbursementDate:e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Duration (months) *</label>
                  <input type="number" value={first.duration} onChange={e=>setFirst({...first, duration:e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Monthly Principal (optional)</label>
                  <input type="number" value={first.monthlyPrincipalPayment} onChange={e=>setFirst({...first, monthlyPrincipalPayment:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Application Fee *</label>
                  <input type="number" value={first.applicationFee} onChange={e=>setFirst({...first, applicationFee:e.target.value})} />
                </div>
              </div>
              
              {previewFirst && (
                <div className="calc-preview">
                  Principal {previewFirst.principal.toLocaleString()} | Interest {previewFirst.interest.toLocaleString()} | Monthly {previewFirst.monthly.toLocaleString()}
                </div>
              )}
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isFirstTabDisabled}>
                  {busy ? 'Submitting...' : 'Submit First Loan'}
                </button>
                {hasExistingLoansForFirstTab && (
                  <span className="action-hint text-danger">Button disabled - Use Top-Up tab instead</span>
                )}
                {!hasExistingLoansForFirstTab && amountExceedsCapacity && (
                  <span className="action-hint text-danger">Button disabled - Amount exceeds capacity</span>
                )}
              </div>
            </form>
          )}

          {tab==='topup' && (
            <form onSubmit={submitTop}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Member *</label>
                  <MemberSearch members={members} value={topMemberId} onChange={setTopMemberId} />
                </div>
                <div className="form-group">
                  <label>Loan Type *</label>
                  <select value={top.loanType} onChange={e=>setTop({...top, loanType:e.target.value})}>
                    <option value="normal">Normal</option>
                    <option value="school">School</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Amount *</label>
                  {remainingCapacity !== null && (
                    <div className={`capacity-hint ${remainingCapacity > 0 ? 'text-success' : 'text-danger'}`}>
                      Can borrow up to: KES {remainingCapacity.toLocaleString()}
                    </div>
                  )}
                  <input 
                    type="number" 
                    value={top.originalAmount} 
                    onChange={e=>setTop({...top, originalAmount:e.target.value})} 
                    className={amountExceedsCapacity ? 'input-error' : ''}
                    required 
                  />
                  {amountExceedsCapacity && (
                    <span className="error-hint">⚠️ Amount exceeds borrowing capacity!</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Disbursement Date *</label>
                  <input type="date" min={TODAY_ISO} value={top.disbursementDate} onChange={e=>setTop({...top, disbursementDate:e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Duration (months) *</label>
                  <input type="number" value={top.duration} onChange={e=>setTop({...top, duration:e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Monthly Principal (optional)</label>
                  <input type="number" value={top.monthlyPrincipalPayment} onChange={e=>setTop({...top, monthlyPrincipalPayment:e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Application Fee *</label>
                  <input type="number" value={top.applicationFee} onChange={e=>setTop({...top, applicationFee:e.target.value})} />
                </div>
              </div>
              
              {previewTop && (
                <div className="calc-preview">
                  Principal {previewTop.principal.toLocaleString()} | Interest {previewTop.interest.toLocaleString()} | Monthly {previewTop.monthly.toLocaleString()}
                </div>
              )}
              
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isTopUpTabDisabled}>
                  {busy ? 'Submitting...' : 'Submit Top-Up'}
                </button>
                {topUpEligibility && !topUpEligibility.canApply && (
                  <span className="action-hint text-danger">Button disabled - Need 75% repayment</span>
                )}
                {(!topUpEligibility || topUpEligibility.canApply) && amountExceedsCapacity && (
                  <span className="action-hint text-danger">Button disabled - Amount exceeds capacity</span>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}